import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { AuthContext } from '../../context/AuthContext';

function AssignmentModal({ assignment, onClose }) {
  const { user } = useContext(AuthContext);
  const accessToken = user?.token || localStorage.getItem('token');

  const [examiners, setExaminers] = useState([]);
  const [selectedExaminers, setSelectedExaminers] = useState([]);
  const [examinerToAdd, setExaminerToAdd] = useState('');
  const [selectedExamCentre, setSelectedExamCentre] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (assignment) {
      // Patch: Accept both camelCase and PascalCase property names
      const examDate = assignment.examDate || assignment.exam_date;
      const startTime = assignment.startTime || assignment.StartTime;
      const durationMinutes = assignment.durationMinutes || assignment.DurationMinutes;
      if (!examDate || !startTime || !durationMinutes) {
        setFeedback({ type: 'error', message: 'Assignment missing date/time/duration.' });
        return;
      }
      axios
        .get('/api/exam-assignments/available-examiners', {
          params: {
            date: examDate,
            startTime,
            durationMinutes
          },
          headers: { Authorization: `Bearer ${accessToken}` }
        })
        .then((response) => {
          setExaminers(response.data);
        })
        .catch((err) => {
          console.error('Error fetching available examiners:', err);
          setFeedback({ type: 'error', message: 'Error fetching available examiners.' });
        });
    }
  }, [accessToken, assignment]);

  // Only centres that actually have candidates registered in this exam's
  // module belong here - examCentreStatuses is already scoped that way by
  // the backend (getActiveExamsReadyToAssign drops any centre with zero
  // registered students for this exam's program/year/semester), so reuse it
  // directly instead of the old, unfiltered /api/exam-centres/with-students
  // call (which returned every centre with ANY students, for ANY module).
  const examCentres = React.useMemo(
    () => (assignment?.examCentreStatuses || []).map((c) => ({
      id: c.examCentreId,
      centreName: c.examCentreName
    })),
    [assignment]
  );

  const getCentreTitle = (centreId) => {
    if (!centreId) return '';
    const found = examCentres.find((c) => String(c.id) === String(centreId));
    return found ? found.centreName || '' : '';
  };

  // Examiners already on this exam+centre must not be re-offered - picking
  // one again is rejected server-side ("Examiner already assigned to this
  // exam centre"), and the pre-check here is what prevents that mistake in
  // the first place rather than just surfacing the error after the fact.
  const alreadyAssignedExaminerIds = React.useMemo(() => {
    if (!selectedExamCentre || !assignment?.examCentreStatuses) return new Set();
    const centreStatus = assignment.examCentreStatuses.find(
      (c) => String(c.examCentreId) === String(selectedExamCentre)
    );
    return new Set((centreStatus?.assignedExaminerIds || []).map(String));
  }, [selectedExamCentre, assignment]);

  useEffect(() => {
    setSelectedExaminers((prev) => prev.filter((id) => !alreadyAssignedExaminerIds.has(String(id))));
  }, [alreadyAssignedExaminerIds]);

  const handleAddExaminer = () => {
    if (!examinerToAdd) return;
    if (!selectedExaminers.includes(examinerToAdd)) {
      setSelectedExaminers((prev) => [...prev, examinerToAdd]);
    }
    setExaminerToAdd('');
  };

  const handleClear = () => {
    setSelectedExaminers([]);
    setSelectedExamCentre('');
    setExaminerToAdd('');
    setFeedback(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!assignment || !(assignment.examId || assignment.id)) {
      setFeedback({ type: 'error', message: 'Assignment details missing.' });
      return;
    }
    if (selectedExaminers.length === 0) {
      setFeedback({ type: 'error', message: 'Please select at least one examiner.' });
      return;
    }
    if (!selectedExamCentre) {
      setFeedback({ type: 'error', message: 'Please select an exam centre.' });
      return;
    }
    setSubmitting(true);

    const examId = assignment.examId || assignment.id;
    const examCentreId = selectedExamCentre;

    // Build payload with examinerIds as an array
    const payload = {
      examId: parseInt(examId, 10),
      examCentreId: parseInt(examCentreId, 10),
      examinerIds: selectedExaminers.filter(Boolean).map(id => parseInt(id, 10))
    };

    axios
      .post('/api/exam-assignments/assign', payload, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`
        }
      })
      .then(() => {
        setFeedback({ type: 'success', message: 'Assignment successful.' });
        setTimeout(() => onClose(), 1500);
      })
      .catch((err) => {
        console.error('Assignment error:', err.response ? err.response.data : err);
        const backendMessage = err.response?.data?.error;
        setFeedback({
          type: 'error',
          message: backendMessage || 'Assignment failed. Please try again.'
        });
        setSubmitting(false);
      });
  };

  const displayName =
    assignment?.examName ||
    assignment?.moduleName ||
    assignment?.examCentreName ||
    '';
  const centreTitle = getCentreTitle(selectedExamCentre);

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {assignment ? (
            <>
              Deployment for <strong>{displayName}</strong>
              {centreTitle && ' at '}
              {centreTitle && <strong>{centreTitle}</strong>}
            </>
          ) : (
            'Loading assignment details...'
          )}
        </Modal.Title>
      </Modal.Header>

      {!assignment ? (
        <Modal.Body className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading assignment details...</p>
          <Button variant="secondary" onClick={onClose} className="mt-3">
            Close
          </Button>
        </Modal.Body>
      ) : (
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {feedback && (
              <Alert variant={feedback.type === 'success' ? 'success' : 'danger'}>
                {feedback.message}
              </Alert>
            )}
            <p className="text-muted" style={{ fontSize: '0.95rem' }}>
              You can deploy more than one examiner for this exam depending on the number of students at the centre.
              And one whom you select first is the Team Leader.. 
            </p>
            <Form.Group controlId="examinerSelect" className="mb-3">
              <Form.Label>Select examiner for this exam</Form.Label>
              <div className="d-flex gap-2">
                <Form.Select
                  value={examinerToAdd}
                  onChange={(e) => setExaminerToAdd(e.target.value)}
                >
                  <option value="">-- Select an Examiner --</option>
                  {examiners
                    .filter((examiner) => !alreadyAssignedExaminerIds.has(String(examiner.id)))
                    .map((examiner) => (
                      <option key={examiner.id} value={examiner.id}>
                        {examiner.username}
                      </option>
                    ))}
                </Form.Select>
                <Button variant="outline-primary" onClick={handleAddExaminer}>
                  Add
                </Button>
              </div>
            </Form.Group>
            {selectedExaminers.length > 0 && (
              <div className="mb-3">
                <Form.Label>Your selections</Form.Label>
                <ul className="list-group">
                  {selectedExaminers.map((examinerId, idx) => {
                    const examinerObj = examiners.find((ex) => ex.id === parseInt(examinerId, 10));
                    const name = examinerObj ? examinerObj.username : examinerId;
                    return (
                      <li key={examinerId} className="list-group-item">
                        {name} {idx === 0 && selectedExaminers.length > 1 && <span style={{color: '#0d6efd'}}>(team leader)</span>}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            <Form.Group controlId="examCentreSelect" className="mb-3">
              <Form.Label>Select Exam Centre</Form.Label>
              <Form.Select
                value={selectedExamCentre}
                onChange={(e) => setSelectedExamCentre(e.target.value)}
              >
                <option value="">-- Select Exam Centre --</option>
                {examCentres.map((centre) => (
                  <option key={centre.id} value={centre.id}>
                    {centre.centreName}
                  </option>
                ))}
              </Form.Select>
              {selectedExamCentre && alreadyAssignedExaminerIds.size > 0 && (
                <Form.Text className="text-muted">
                  Already assigned here: {
                    examiners
                      .filter((ex) => alreadyAssignedExaminerIds.has(String(ex.id)))
                      .map((ex) => ex.username)
                      .join(', ') || `${alreadyAssignedExaminerIds.size} examiner(s)`
                  } - not shown above.
                </Form.Text>
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="success"
              type="submit"
              disabled={submitting}
              style={{ minWidth: '100px' }}
            >
              {submitting ? 'Deploying...' : 'Deploy'}
            </Button>
            <Button
              variant="info"
              onClick={handleClear}
              disabled={submitting}
              style={{ minWidth: '100px', color: 'white' }}
            >
              Clear
            </Button>
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={submitting}
              style={{ minWidth: '100px' }}
            >
              Close
            </Button>
          </Modal.Footer>
        </Form>
      )}
    </Modal>
  );
}

export default AssignmentModal;
